// Shared client-side validation + error display for AJAX modal forms
// (form.v3-modal-form, spawned by global_modal_v3_controller.js). One place
// wires every popup instead of duplicating submit/validation JS per view --
// see docs/givsum3_08_july.md Section 9.
//
// Two responsibilities:
//   1. Required-field validation on submit, shown inline via this design
//      system's `.is-invalid` / `.invalid-feedback-msg` classes instead of
//      the browser's native (and inconsistent) validation bubble.
//   2. For `remote: true` forms, a proper inline banner for the server's
//      validation error (tickets_controller etc. respond 422 with
//      `{ action: { error: "..." } }` -- previously never surfaced client-side).

const SYNTHETIC_ERROR_ATTR = "data-gv-synthetic"

function groupAnchor(form, name) {
  const fields = form.querySelectorAll(`[name="${CSS.escape(name)}"]`)
  return fields[fields.length - 1] || null
}

function ensureFieldErrorNode(field) {
  const next = field.nextElementSibling
  if (next && next.classList.contains("invalid-feedback-msg")) return next

  const node = document.createElement("div")
  node.className = "invalid-feedback-msg"
  node.setAttribute(SYNTHETIC_ERROR_ATTR, "true")
  field.insertAdjacentElement("afterend", node)
  return node
}

function showFieldError(field, message) {
  if (!field) return
  field.classList.add("is-invalid")
  ensureFieldErrorNode(field).textContent = message
}

function clearFieldError(field) {
  if (!field) return
  field.classList.remove("is-invalid")
  const next = field.nextElementSibling
  if (next && next.classList.contains("invalid-feedback-msg") && next.hasAttribute(SYNTHETIC_ERROR_ATTR)) {
    next.remove()
  }
}

function ensureFormErrorBanner(form) {
  let banner = form.querySelector(":scope > .v3-modal-form-error")
  if (!banner) {
    banner = document.createElement("div")
    banner.className = "v3-modal-form-error alert alert-danger py-2 px-3 mb-0 d-none"
    banner.setAttribute("role", "alert")
    form.insertBefore(banner, form.firstChild)
  }
  return banner
}

function showFormError(form, message) {
  const banner = ensureFormErrorBanner(form)
  banner.textContent = message
  banner.classList.remove("d-none")
}

function clearFormError(form) {
  const banner = form.querySelector(":scope > .v3-modal-form-error")
  if (banner) banner.classList.add("d-none")
}

function showInvalidFields(form) {
  const seenGroups = new Set()
  let firstInvalid = null

  Array.from(form.elements).forEach((field) => {
    if (!field.willValidate) return

    if (field.checkValidity()) {
      if (field.type !== "radio" && field.type !== "checkbox") clearFieldError(field)
      return
    }

    const grouped = (field.type === "radio" || field.type === "checkbox") && field.name
    if (grouped) {
      if (seenGroups.has(field.name)) return
      seenGroups.add(field.name)
    }

    firstInvalid ||= field
    showFieldError(grouped ? groupAnchor(form, field.name) : field, field.validationMessage)
  })

  if (firstInvalid) firstInvalid.focus()
}

function extractServerError(response) {
  if (response && response.action && response.action.error) return response.action.error
  if (typeof response === "string" && response.trim()) return response
  return "Something went wrong. Please check your entries and try again."
}

// Wires every unwired `.v3-modal-form` inside `container`. Safe to call
// repeatedly (e.g. once per spawned modal) -- already-wired forms are skipped.
export function wireModalFormValidation(container) {
  container.querySelectorAll("form.v3-modal-form").forEach((form) => {
    if (form.dataset.gvValidationWired) return
    form.dataset.gvValidationWired = "true"

    // We render our own inline messages -- suppress the native bubble.
    form.setAttribute("novalidate", "novalidate")

    form.addEventListener("submit", (event) => {
      clearFormError(form)
      if (!form.checkValidity()) {
        event.preventDefault()
        event.stopPropagation()
        showInvalidFields(form)
      }
    })

    // Clear a field's error as soon as the user fixes it.
    form.addEventListener("input", (event) => {
      const field = event.target
      if (field.willValidate && field.checkValidity() && field.type !== "radio" && field.type !== "checkbox") {
        clearFieldError(field)
      }
    })
    form.addEventListener("change", (event) => {
      const field = event.target
      if (!field.willValidate || !field.checkValidity()) return
      const grouped = field.type === "radio" || field.type === "checkbox"
      clearFieldError(grouped ? groupAnchor(form, field.name) : field)
    })

    // rails-ujs only fires ajax:* on `remote: true` forms -- inert otherwise.
    form.addEventListener("ajax:error", (event) => {
      const [response] = event.detail || []
      showFormError(form, extractServerError(response))
    })
    form.addEventListener("ajax:success", () => clearFormError(form))
  })
}
